import {
  Children,
  createElement,
  Dispatch,
  forwardRef,
  isValidElement,
  MutableRefObject,
  ReactNodeArray,
  SetStateAction,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { ReactKeyboardEvent } from '../../react-synthetic-events';
import { useDesignTokens } from '../../theme';
import { setRef } from '../../util/ref-util';
import { ListDesignProps } from './';
import { getListTokens } from './__internal__';

import cx from 'classnames';
import './List.css';
import cn from './List.css.json';

/**
 * The ListDesign component is a building block for creating other components. Specifically,
 * it is for building lists that don’t contain interactive elements. For example, it is
 * used by the Combo Box component’s drop-down list.
 *
 * This component lacks major features and accessibility support. Consumers who need to
 * create standalone lists or who need to create list items that contain interactive
 * elements such as buttons should use the List component instead.
 *
 * @param {ListDesignProps} props the ListDesignProps properties
 * @returns the ListDesign element
 */
export const ListDesign = forwardRef<HTMLUListElement, ListDesignProps>(function ListDesign(
  props,
  ref
) {
  const ulRef = useRef<HTMLUListElement | null>(null);
  const values = useMemo(() => {
    if (!props.children) {
      return props.children;
    }
    // Remove separators that were passed in to make keyboard nav easier
    return Children.toArray(props.children).filter(
      (element) =>
        isValidElement(element) &&
        element.type &&
        (element.type as { name: string })?.name !== 'ListSeparator'
    );
  }, [props.children]);
  const handleKeyDown = useListKeyDownHandler({
    length: (values as ReactNodeArray).length,
    selectedIndex: props.selectedIndex,
    setFocusedIndex: props.setFocusedIndex,
    onSelectFocusedItem: props.onSelectFocusedItem,
    ulRef,
  });
  const dt = useDesignTokens();
  const tokenStyle = useMemo(
    () => getListTokens(dt, props.theme, props.font, props.variant, props.whiteSpace),
    [dt, props.theme, props.font, props.variant, props.whiteSpace]
  );

  return (
    <ul
      ref={(ul) => {
        ulRef.current = ul;
        setRef(ref, ul);
      }}
      className={cx(props.className, !!props.theme && cn.themeOverride, cn.list)}
      style={{
        ...tokenStyle,
        ...props.style,
      }}
      role={props.role ?? 'listbox'}
      aria-label={props.ariaLabel}
      tabIndex={props.focusable === false ? -1 : undefined}
      aria-labelledby={
        Array.isArray(props.ariaLabelledBy)
          ? props.ariaLabelledBy.length
            ? props.ariaLabelledBy.join(' ')
            : undefined
          : props.ariaLabelledBy
      }
      data-testid={props.testId}
      onKeyDown={handleKeyDown}
      id={props.id}
    >
      {props.children}
    </ul>
  );
});

export const useListKeyDownHandler = ({
  length,
  doLooping,
  selectedIndex,
  setFocusedIndex,
  onSelectFocusedItem,
  ulRef,
}: {
  length: number;
  doLooping?: boolean;
  selectedIndex?: number;
  setFocusedIndex?: Dispatch<SetStateAction<number | null | undefined>>;
  onSelectFocusedItem?: () => void;
  ulRef?: MutableRefObject<HTMLUListElement | null>;
}) =>
  useCallback(
    (e: ReactKeyboardEvent) => {
      if (length <= 0) {
        return;
      }
      const maxIndex = length - 1;

      const getPrevFocusedIndex = (prevFocusedIndex: number | null | undefined) =>
        prevFocusedIndex ?? selectedIndex;

      // This assumes the parent is the scrolling container. Otherwise,
      // if later needs for a different scrolling container, then provide
      // a new ref prop to the scrolling container. For now, all known
      // use cases have a parent scrolling container.
      const getItemsPerPage = () => {
        const parentElement = ulRef?.current?.parentElement;
        const scrollHeight = parentElement?.scrollHeight ?? 0;
        const pageHeight = parentElement?.offsetHeight ?? 0;
        const itemHeight = scrollHeight / length;
        // Default items per page is set to 10 according to the spec
        return Math.round(itemHeight !== 0 && pageHeight !== 0 ? pageHeight / itemHeight : 10);
      };

      const isScrollable = (elem: HTMLElement): boolean => {
        const hasScrollableContent = elem.scrollHeight > elem.clientHeight;
        const overflowYStyle = window.getComputedStyle(elem).overflowY;
        const isOverflowHidden = overflowYStyle.indexOf('hidden') !== -1;
        return hasScrollableContent && !isOverflowHidden;
      };

      const getScrollableParent = (elem: HTMLElement): HTMLElement | undefined => {
        return !elem || elem === document.body
          ? document.body
          : isScrollable(elem)
          ? elem
          : getScrollableParent(elem.parentNode as HTMLElement);
      };

      // Scroll item into view to the ideal location first
      // before the focus (if any), otherwise target item may
      // centered in the middle of the viewpointPropTypes.
      // Note that item may not get focused.
      const scrollIntoView = (index: number, alignToTop: boolean) =>
        ulRef?.current?.children[index]?.scrollIntoView?.(alignToTop);

      const isDomItemVisible = function (index: number) {
        const elem: HTMLLIElement = ulRef?.current?.children[index] as HTMLLIElement;
        const parent = getScrollableParent(ulRef?.current as HTMLElement);

        if (!elem || !parent) {
          return false;
        }

        const itemRect = elem.getBoundingClientRect();
        const parentRect = parent.getBoundingClientRect();

        return itemRect.top >= parentRect.top && itemRect.bottom <= parentRect.bottom;
      };

      switch (e.key) {
        case 'ArrowDown':
          setFocusedIndex?.((prevFocusedIndex) => {
            const prevIndex = getPrevFocusedIndex(prevFocusedIndex) ?? -1;
            const index = prevIndex < length - 1 ? prevIndex + 1 : doLooping ? 0 : length - 1;
            if (!isDomItemVisible(index)) {
              scrollIntoView(index, false);
            }
            return index;
          });
          break;
        case 'ArrowUp':
          setFocusedIndex?.((prevFocusedIndex) => {
            const prevIndex = getPrevFocusedIndex(prevFocusedIndex) ?? length;
            const index = prevIndex > 0 ? prevIndex - 1 : doLooping ? length - 1 : 0;
            if (!isDomItemVisible(index)) {
              scrollIntoView(index, true);
            }
            return index;
          });
          break;
        case 'PageDown': {
          const itemsPerPage = getItemsPerPage();
          setFocusedIndex?.((prevFocusedIndex) => {
            const prevIndex = getPrevFocusedIndex(prevFocusedIndex) ?? -1;
            const isLast = prevIndex === maxIndex;
            const index = Math.min(prevIndex + itemsPerPage, isLast && doLooping ? 0 : maxIndex);
            scrollIntoView(index, false);
            return index;
          });
          break;
        }
        case 'PageUp':
          {
            const itemsPerPage = getItemsPerPage();
            setFocusedIndex?.((prevFocusedIndex) => {
              const prevIndex = getPrevFocusedIndex(prevFocusedIndex) ?? 0;
              const isFirst = prevIndex === 0;
              const index = Math.max(prevIndex - itemsPerPage, isFirst && doLooping ? maxIndex : 0);
              scrollIntoView(index, true);
              return index;
            });
          }
          break;
        case 'End':
          {
            const index = length - 1;
            scrollIntoView(index, false);
            setFocusedIndex?.(index);
          }
          break;
        case 'Home':
          {
            const index = 0;
            scrollIntoView(index, true);
            setFocusedIndex?.(index);
          }
          break;
        case ' ': /* falls-through */
        case 'Enter':
          // TODO: HC fires a different event here
          onSelectFocusedItem?.();
          break;
        default:
          return; // NOTE: early return
      }
      e.preventDefault();
    },
    [length, selectedIndex, setFocusedIndex, onSelectFocusedItem, doLooping, ulRef]
  );
